import './assets/styles.css';

import { Command, Plugin } from '@ckeditor/ckeditor5-core';
import { ButtonView } from '@ckeditor/ckeditor5-ui';
import { Widget, toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget';

export default class FourColumsBox extends Plugin {
    static get requires() {
        return [ FourColumnsBoxEditing, FourColumnsBoxUi ];
    }
}

class FourColumnsBoxUi extends Plugin {
    init() {
        const editor = this.editor;
        const t = editor.t;

        editor.ui.componentFactory.add( 'FourColumnsBox', locale => {
            // The state of the button will be bound to the widget command.
            const command = editor.commands.get( 'insertFourColumnsBox' );

            // The button will be an instance of ButtonView.
            const buttonView = new ButtonView( locale );

            buttonView.set( {
                // The t() function helps localize the editor. All strings enclosed in t() can be
                // translated and change when the language of the editor changes.
                label: t( '4☐' ),
                withText: true,
                tooltip: true
            } );

            // Bind the state of the button to the command.
            buttonView.bind( 'isOn', 'isEnabled' ).to( command, 'value', 'isEnabled' );

            // Execute the command when the button is clicked (executed).
            this.listenTo( buttonView, 'execute', () => editor.execute( 'insertFourColumnsBox' ) );

            return buttonView;
        } );
    }
}

class FourColumnsBoxEditing extends Plugin {
    static get requires() {
        return [ Widget ];
    }

    init() {

        this._defineSchema();
        this._defineConverters();

        this.editor.commands.add( 'insertFourColumnsBox', new InsertFourColumnsBoxCommand( this.editor ) );
    }

    _defineSchema() {
        const schema = this.editor.model.schema;

        schema.register( 'FourColumnsBox', {
      
            inheritAllFrom: '$blockObject'
        } );

        schema.register( 'FourColumnsBoxCol1', {
        
            isLimit: true,

            allowIn: 'FourColumnsBox',

         
            allowContentOf: '$root'
        } );

        schema.register( 'FourColumnsBoxCol2', {
          
            isLimit: true,

            allowIn: 'FourColumnsBox',

  
            allowContentOf: '$root'
        } );

        schema.register( 'FourColumnsBoxCol3', {
          
            isLimit: true,

            allowIn: 'FourColumnsBox',

  
            allowContentOf: '$root'
        } );

        schema.register( 'FourColumnsBoxCol4', {
          
            isLimit: true,

            allowIn: 'FourColumnsBox',

  
            allowContentOf: '$root'
        } );

        schema.addChildCheck( ( context, childDefinition ) => {
            if ( context.endsWith( 'FourColumnsBoxCol4' ) && childDefinition.name == 'FourColumnsBox' ) {
                return false;
            }
        } );
    }

    _defineConverters() {
        const conversion = this.editor.conversion;

        // <simpleBox> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'FourColumnsBox',
            view: {
                name: 'section',
                classes: 'four-columns-box'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'FourColumnsBox',
            view: {
                name: 'section',
                classes: 'four-columns-box'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'FourColumnsBox',
            view: ( modelElement, { writer: viewWriter } ) => {
                const section = viewWriter.createContainerElement( 'section', { class: 'four-columns-box' } );

                return toWidget( section, viewWriter, { label: 'Four columns widget' } );
            }
        } );

        // <simpleBoxTitle> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'FourColumnsBoxCol1',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol1',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol1',
            view: ( modelElement, { writer: viewWriter } ) => {
               
                const div = viewWriter.createEditableElement( 'div', { class: 'four-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );

        // <simpleBoxDescription> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'FourColumnsBoxCol2',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol2',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol2',
            view: ( modelElement, { writer: viewWriter } ) => {
            
                const div = viewWriter.createEditableElement( 'div', { class: 'four-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );

        conversion.for( 'upcast' ).elementToElement( {
            model: 'FourColumnsBoxCol3',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol3',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol3',
            view: ( modelElement, { writer: viewWriter } ) => {
            
                const div = viewWriter.createEditableElement( 'div', { class: 'four-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );

        conversion.for( 'upcast' ).elementToElement( {
            model: 'FourColumnsBoxCol4',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol4',
            view: {
                name: 'div',
                classes: 'four-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'FourColumnsBoxCol4',
            view: ( modelElement, { writer: viewWriter } ) => {
            
                const div = viewWriter.createEditableElement( 'div', { class: 'four-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );
    }
}

class InsertFourColumnsBoxCommand extends Command {
    execute() {
        this.editor.model.change( writer => {
            this.editor.model.insertObject( createFourColumnsBox( writer ) );
        } );
    }

    refresh() {
        const model = this.editor.model;
        const selection = model.document.selection;
        const allowedIn = model.schema.findAllowedParent( selection.getFirstPosition(), 'FourColumnsBox' );

        this.isEnabled = allowedIn !== null;
    }
}

function createFourColumnsBox( writer ) {
    const FourColumnsBox = writer.createElement( 'FourColumnsBox' );
    const FourColumnsBoxCol1 = writer.createElement( 'FourColumnsBoxCol1' );
    const FourColumnsBoxCol2 = writer.createElement( 'FourColumnsBoxCol2' );
    const FourColumnsBoxCol3 = writer.createElement( 'FourColumnsBoxCol3' );
    const FourColumnsBoxCol4 = writer.createElement( 'FourColumnsBoxCol4' );

    writer.append( FourColumnsBoxCol1, FourColumnsBox );
    writer.append( FourColumnsBoxCol2, FourColumnsBox );
    writer.append( FourColumnsBoxCol3, FourColumnsBox );
    writer.append( FourColumnsBoxCol4, FourColumnsBox );

    // There must be at least one paragraph for the description to be editable.
    // See https://github.com/ckeditor/ckeditor5/issues/1464.
    writer.appendElement( 'paragraph', FourColumnsBoxCol1 );
    writer.appendElement( 'paragraph', FourColumnsBoxCol2 );
    writer.appendElement( 'paragraph', FourColumnsBoxCol3 );
    writer.appendElement( 'paragraph', FourColumnsBoxCol4 );

    return FourColumnsBox;
}
